import { yupResolver } from "@hookform/resolvers/yup";
import { FilesUploader, If } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { ProductsFieldForForms } from "modules/products";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, FormField, Input, MediaFilePicker } from "ui";

import { productSetValidationSchema } from "../../../helpers";
import { ProductSetFormShowOnField } from "./atoms";
import { ProductSetFormProps } from "./ProductSetForm.types";

import styles from "./ProductSetForm.module.scss";

const ProductSetForm = ({
  formId,
  defaultValues,
  onSubmit
}: ProductSetFormProps) => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues,
    resolver: yupResolver(productSetValidationSchema) as any
  });

  const coverMedia = watch("coverMedia");
  const isVideo = useMemo(
    () => coverMedia?.mediaType === MediaType.VIDEO,
    [coverMedia]
  );

  return (
    <form className={styles.form} id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="coverMedia"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormField label={"Banner"} errorMessage={error?.message}>
            <FilesUploader
              onSuccessUploading={onChange}
              uploadDirectoryKey={MediaDirectoryKeys.ProductSet}
            >
              {(uploaderProps) => (
                <MediaFilePicker
                  {...uploaderProps}
                  value={value}
                  customSize={{ width: 150, height: 105 }}
                  label={"375:262"}
                  onDeleteMediaFile={() => onChange(null)}
                />
              )}
            </FilesUploader>
          </FormField>
        )}
      />

      <If condition={isVideo}>
        <Controller
          control={control}
          name="allowCoverMediaDisplayInFullScreenMode"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={value ?? true}
              onChange={(e) => onChange(e.target.checked)}
              label="Allow cover media display in full screen mode"
            />
          )}
        />
      </If>

      <Input
        label={"Title (optional)"}
        {...register("title")}
        errorMessage={errors.title?.message}
      />

      <Input
        label={"Description (optional)"}
        asTag="textarea"
        {...register("description")}
        errorMessage={errors.description?.message}
      />

      <Controller
        control={control}
        name="showOnHome"
        render={({ field: { value, onChange } }) => (
          <ProductSetFormShowOnField
            value={value}
            onChange={onChange}
            title="Show on Home"
            description="When enabled, the banner will appear in the slider on the Home page of the app."
          />
        )}
      />
      <Controller
        control={control}
        name="showInSearch"
        render={({ field: { value, onChange } }) => (
          <ProductSetFormShowOnField
            value={value}
            onChange={onChange}
            title="Show in Search"
            description="When enabled, the banner will appear in the search results."
          />
        )}
      />

      <div className={styles.form_separatorLine} />

      <Controller
        control={control}
        name="productVariants"
        render={({ field: { value, onChange } }) => (
          <ProductsFieldForForms
            productsList={value}
            onChangeProductsList={onChange}
            errorMessage={errors.productVariants?.message}
            withProductSetDetailsModal={false}
          />
        )}
      />
    </form>
  );
};

export default ProductSetForm;
